import { BRAND } from '@app/shared';
import { clsx } from 'clsx';
import { useEffect, useId, useState } from 'react';

/** Logo : pastille bleue en relief + nom. */
export function Logo({ className, size = 32 }: { className?: string; size?: number }) {
  // Identifiant unique : plusieurs logos coexistent (barre latérale masquée sur téléphone, en-tête…).
  const gradient = useId();
  return (
    <span className={clsx('inline-flex items-center gap-2.5', className)}>
      <svg
        width={size}
        height={size}
        viewBox="0 0 32 32"
        aria-hidden="true"
        className="shrink-0 rounded-[10px] shadow-clay-primary-sm"
      >
        <defs>
          <linearGradient id={gradient} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="oklch(0.66 0.18 254)" />
            <stop offset="0.55" stopColor="oklch(0.585 0.188 256)" />
            <stop offset="1" stopColor="oklch(0.52 0.19 258)" />
          </linearGradient>
        </defs>
        <rect width="32" height="32" rx="10" fill={`url(#${gradient})`} />
        <path d="M10 8h3.6v6.6L19.4 8h4.2l-6.4 7 6.8 9h-4.4l-4.6-6.4-1.4 1.5V24H10z" fill="#fff" />
      </svg>
      <span className="font-display text-[1.35rem] leading-none font-extrabold tracking-tight text-ink">
        {BRAND.name}
      </span>
    </span>
  );
}

const DEMO_LINES = [
  ['Ta', 'vidéo', 'traduite'],
  ['dans', 'cinq', 'langues'],
  ['en', 'deux', 'clics'],
];

function useReducedMotion() {
  const [reduced, setReduced] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches,
  );
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onChange = () => setReduced(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return reduced;
}

/**
 * Téléphone de démonstration : une vidéo verticale stylisée et des sous-titres qui s'allument
 * mot par mot, comme ceux que produit l'outil. Décoratif (masqué aux lecteurs d'écran).
 */
export function PhoneMock({ className }: { className?: string }) {
  const reduced = useReducedMotion();
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (reduced) return;
    const t = window.setInterval(() => setTick((v) => v + 1), 520);
    return () => window.clearInterval(t);
  }, [reduced]);
  const words = DEMO_LINES.flat().length;
  const step = reduced ? 1 : tick % words;
  const line = Math.floor(step / 3);
  const word = step % 3;

  return (
    <div aria-hidden="true" className={clsx('relative', className)}>
      <div className="relative aspect-[9/16] w-full overflow-hidden rounded-[2.2rem] bg-[oklch(0.3_0.04_260)] p-2 shadow-clay-lg">
        <div className="relative size-full overflow-hidden rounded-[1.7rem] bg-[radial-gradient(120%_80%_at_30%_15%,oklch(0.82_0.12_70),oklch(0.62_0.16_30)_45%,oklch(0.35_0.1_300)_100%)]">
          {/* Silhouette floue : la « personne » de la vidéo. */}
          <div className="absolute top-[22%] left-1/2 size-[42%] -translate-x-1/2 rounded-full bg-[oklch(0.3_0.05_40/0.55)] blur-md" />
          <div className="absolute top-[52%] left-1/2 h-[60%] w-[78%] -translate-x-1/2 rounded-t-[45%] bg-[oklch(0.25_0.05_300/0.6)] blur-md" />
          <div className="absolute inset-x-0 top-[62%] flex justify-center px-3">
            <p className="text-center text-[clamp(1rem,2.2vw,1.45rem)] leading-tight font-black tracking-tight text-white uppercase [font-family:'Caption_Poppins_ExtraBold','Nunito_Variable',sans-serif] [text-shadow:0_2px_0_rgb(0_0_0/0.55),0_0_10px_rgb(0_0_0/0.35)]">
              {DEMO_LINES[line]?.map((w, i) => (
                <span
                  key={w}
                  className={clsx(
                    'mx-[0.14em] inline-block transition-[color,transform] duration-150',
                    i === word && 'scale-110 text-[#FFE14D]',
                  )}
                >
                  {w}
                </span>
              ))}
            </p>
          </div>
          <div className="absolute inset-x-4 bottom-4 h-1 overflow-hidden rounded-full bg-white/30">
            <div
              className="h-full rounded-full bg-white transition-[width] duration-500 ease-linear"
              style={{ width: `${((step + 1) / words) * 100}%` }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
